import type { ComponentPropsWithoutRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface MarkdownReportProps {
  content: string;
  className?: string;
  tableClassName?: string;
  tableLabel?: string;
}

export function MarkdownReport({
  content,
  className = 'markdown-report',
  tableClassName,
  tableLabel = '数据表格，可横向滚动',
}: MarkdownReportProps) {
  return (
    <ReactMarkdown
      className={className}
      remarkPlugins={[remarkGfm]}
      components={{
        a: SafeLink,
        img: SafeImage,
        table: tableClassName
          ? ({ node: _node, children, ...props }) => (
              <div
                className={tableClassName}
                role="region"
                aria-label={tableLabel}
                tabIndex={0}
              >
                <table {...props}>{children}</table>
              </div>
            )
          : undefined,
      }}
    >
      {content}
    </ReactMarkdown>
  );
}

function SafeLink({ href, children, ...props }: ComponentPropsWithoutRef<'a'>) {
  if (href && !isSafeLink(href)) {
    return <span className="blocked-link">{children}</span>;
  }
  const external = href?.startsWith('http://') || href?.startsWith('https://');
  return (
    <a
      {...props}
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noreferrer noopener' : undefined}
    >
      {children}
    </a>
  );
}

function isSafeLink(href: string): boolean {
  return (
    href.startsWith('/api/') ||
    href.startsWith('https://') ||
    href.startsWith('http://') ||
    href.startsWith('mailto:') ||
    href.startsWith('#')
  );
}

function SafeImage({ src, alt, ...props }: ComponentPropsWithoutRef<'img'>) {
  if (!src || !isPreviewableImageSource(src)) {
    return <span className="blocked-image">图像路径不可直接预览{alt ? `：${alt}` : ''}</span>;
  }
  return <img {...props} src={src} alt={alt ?? ''} loading="lazy" />;
}

function isPreviewableImageSource(src: string): boolean {
  return (
    src.startsWith('/api/') ||
    src.startsWith('https://') ||
    src.startsWith('http://') ||
    src.startsWith('blob:') ||
    src.startsWith('data:image/')
  );
}
